import { MultiStackedBarChart } from "@fluentui/react-charting";
import { Divider, makeStyles, Text, Tooltip } from "@fluentui/react-components";
import React from "react";
import { IResourceDetails } from "./ResourceAvailability.types";

export interface ISegment {
  pct: number;
  color: string;
  label: string;
  detailKey: keyof IResourceDetails;
  count: number;
}

interface IResourceProgressBarProps {
  segments: ISegment[];
  details?: IResourceDetails;
}

const ResourceProgressBar: React.FC<IResourceProgressBarProps> = ({
  segments,
  details,
}) => {
  const classes = useStyles();

  const chartData = [
    {
      chartData: segments.map((s) => ({
        legend: s.label,
        data: s.count,
        color: s.color,
      })),
    },
  ];

  const renderTooltipContent = (
    detailKey: keyof IResourceDetails,
    label: string,
    color: string,
    count: number
  ) => {
    const header = (
      <div className={classes.tooltipHeaderSection}>
        <span
          className={classes.tooltipColorLine}
          style={{ backgroundColor: color }}
        />
        <div className={classes.tooltipHeaderContent}>
          <Text size={200} weight="semibold">
            {label}
          </Text>
          <Text size={300} weight="bold">
            {count}
          </Text>
        </div>
      </div>
    );

    const items = details?.[detailKey] ?? [];

    if (!details || items.length === 0) {
      return <div className={classes.tooltipContainer}>{header}</div>;
    }

    return (
      <div className={classes.tooltipContainer}>
        {header}
        <Divider className={classes.tooltipDivider} />
        <div className={classes.tooltipDetails}>
          {items.map((item) => (
            <Text key={item.name} size={100} block>
              {item.name}
            </Text>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className={classes.progressBar}>
      <MultiStackedBarChart
        data={chartData}
        hideLabels={true}
        hideRatio={[true]}
        hideLegend={true}
        hideTooltip={true}
        barHeight={6}
        styles={{
          chartTitle: { display: "none" },
          root: { margin: 0, padding: 0 },
          chart: { margin: 0, padding: 0 },
        }}
      />
      <div className={classes.tooltipOverlay}>
        {segments.map((s) => (
          <Tooltip
            key={s.detailKey}
            content={renderTooltipContent(
              s.detailKey,
              s.label,
              s.color,
              s.count
            )}
            relationship="description"
            positioning="above"
          >
            <div
              className={classes.tooltipTrigger}
              style={{ width: `${s.pct}%` }}
            />
          </Tooltip>
        ))}
      </div>
    </div>
  );
};

export default ResourceProgressBar;

const useStyles = makeStyles({
  progressBar: {
    position: "relative",
    width: "100%",
    height: "6px",
    overflow: "visible",
    marginBottom: "8px",
    padding: "0px 5px",
    boxSizing: "border-box",
  },
  tooltipOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    display: "flex",
  },
  tooltipTrigger: {
    height: "100%",
    cursor: "pointer",
  },
  tooltipContainer: {
    padding: "4px",
    width: "fit-content",
  },
  tooltipHeaderSection: {
    display: "flex",
    alignItems: "stretch",
    gap: "8px",
    marginBottom: "6px",
  },
  tooltipColorLine: {
    width: "4px",
    borderRadius: "2px",
    display: "block",
    alignSelf: "stretch",
  },
  tooltipHeaderContent: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  tooltipDivider: {
    marginTop: "6px",
    marginBottom: "4px",
  },
  tooltipDetails: {
    paddingTop: "2px",
  },
  tooltipName: {
    paddingLeft: "4px",
  },
});
